import React from 'react';
import { Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from "@chakra-ui/react";
import { CiSearch } from "react-icons/ci";
import { ManagementTypes } from '../utils/ManagementTypes';

const POLICY_TYPE_LABELS = {
  INDIVIDUALPOLICY: 'Police individuelle',
  FLEETPOLICY: 'Police flotte',
};

/**
 * Nouveau Sinistre Form Component
 * This form matches the new comprehensive claim creation API structure
 */
const NouveauSinistreForm = ({ 
  classes, 
  isPolicySearching,
  foundPolicy,
  onPolicyNumberChange,
  handlePolicySearch,
  handleThirdPartySearch,
  agencies,
  coverageType,
  claimTypeOptions,
  expertOptions,
  policyUsages = []
}) => {
  // Auto-fill "Libellé agence" with the declaration agency. The DAAQ is built
  // server-side by the gateway from the agency code (same rule as the MAE ETL).
  const handleDeclarationAgencyChange = (event) => {
    const selectedAgencyId = event.target.value;
    const agencyLabelSelect = document.querySelector('select[name="agencyLabel"]');

    if (agencyLabelSelect && selectedAgencyId) {
      const selectedAgency = agencies.find(agency => agency._id === selectedAgencyId);
      if (selectedAgency) {
        const agencyName = selectedAgency.name || selectedAgency.label || selectedAgency.code || selectedAgency._id;
        agencyLabelSelect.value = agencyName;
      }
    }
  };

  return (
    <Accordion allowMultiple defaultIndex={[0, 1]}>
      {/* ========== CONTRACT SECTION ========== */}
      <AccordionItem>
        <AccordionButton style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: "transparent", border: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <AccordionIcon style={{ background: "rgba(240, 243, 199, 1)", width: 25, height: 25, borderRadius: 5, color: "rgba(187, 200, 8, 1)" }} />
            <div className={classes.titreLabel}>Données contrat</div>
          </div>
        </AccordionButton>
        <AccordionPanel style={{ padding: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* Basic Policy Info */}
            <div>
              <div className={classes.modalLabel}>N° police *</div>
              <div className={classes.policeInputContainer}>
                <input 
                  name="policyNumber"
                  className={classes.selectInputDialog}
                  style={{ paddingRight: 40 }}
                  disabled={isPolicySearching}
                  onChange={() => foundPolicy && onPolicyNumberChange?.()}
                />
                <button 
                  className={classes.policeSearchBtn}
                  onClick={handlePolicySearch}
                  disabled={isPolicySearching}
                  type="button"
                  style={{
                    opacity: isPolicySearching ? 0.6 : 1,
                    cursor: isPolicySearching ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isPolicySearching ? (
                    <span style={{ color: 'white', fontSize: 10 }}>...</span>
                  ) : (
                    <CiSearch style={{ color: 'white', width: 16, height: 16 }} />
                  )}
                </button>
              </div>
              <input type="hidden" name="foundPolicyNumber" value={foundPolicy?.policyNumber || ''} />
              {foundPolicy && (
                <div style={{ fontSize: 10, color: 'green', marginTop: 4, fontFamily: 'Poppins' }}>
                  ✓ Police trouvée : {foundPolicy.policyNumber}
                  {POLICY_TYPE_LABELS[foundPolicy.policyType] ? ` (${POLICY_TYPE_LABELS[foundPolicy.policyType]})` : ''}
                </div>
              )}
            </div>
            
            <div>
              <div className={classes.modalLabel}>Type de police</div>
              <select name="policyType" className={classes.selectInputDialog} defaultValue="">
                <option value="">Choisir</option>
                <option value="INDIVIDUALPOLICY">Police individuelle</option>
                <option value="FLEETPOLICY">Police flotte</option>
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Usage (création de police)</div>
              <select name="policyUsage" className={classes.selectInputDialog} defaultValue="">
                <option value="">Choisir</option>
                {policyUsages.map((usage) => (
                  <option key={usage._id} value={usage._id}>{usage.label || usage.usageCode}</option>
                ))}
              </select>
            </div>
            
            {/* Policy Holder Section */}
            <div style={{ gridColumn: "1 / -1", marginTop: 12, marginBottom: 8 }}>
              <div style={{ 
                fontFamily: 'Poppins', 
                fontWeight: 600, 
                fontSize: 11, 
                color: 'rgba(63, 66, 84, 0.8)',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                paddingBottom: 6
              }}>
                Informations du souscripteur
              </div>
            </div>
            
            <div>
              <div className={classes.modalLabel}>Code souscripteur</div>
              <input name="policyHolderCode" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>N° identité</div>
              <input name="policyHolderIdentityNumber" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Prénom souscripteur</div>
              <input name="policyHolderFirstName" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Nom souscripteur</div>
              <input name="policyHolderLastName" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Date de naissance</div>
              <input name="policyHolderBirthDate" type="date" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Rôle souscripteur</div>
              <select name="policyHolderRole" className={classes.selectInputDialog} defaultValue="InsuredDriver">
                <option value="InsuredDriver">Conducteur assuré (défaut)</option>
                <option value="InsuredPassenger">Passager assuré</option>
                <option value="Pedestrian">Piéton</option>
                <option value="Witness">Témoin</option>
              </select>
            </div>
            
            {/* Policy Dates Section */}
            <div style={{ gridColumn: "1 / -1", marginTop: 12, marginBottom: 8 }}>
              <div style={{ 
                fontFamily: 'Poppins', 
                fontWeight: 600, 
                fontSize: 11, 
                color: 'rgba(63, 66, 84, 0.8)',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                paddingBottom: 6
              }}>
                Dates de la police
              </div>
            </div>
            
            <div>
              <div className={classes.modalLabel}>Date d'effet</div>
              <input name="policyEffectDate" type="date" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Date d'expiration</div>
              <input name="policyExpirationDate" type="date" className={classes.selectInputDialog} />
            </div>
            
            {/* Vehicle Information Section */}
            <div style={{ gridColumn: "1 / -1", marginTop: 12, marginBottom: 8 }}>
              <div style={{ 
                fontFamily: 'Poppins', 
                fontWeight: 600, 
                fontSize: 11, 
                color: 'rgba(63, 66, 84, 0.8)',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                paddingBottom: 6
              }}>
                Informations du véhicule
              </div>
            </div>
            
            <div>
              <div className={classes.modalLabel}>Immatriculation</div>
              <input name="vehicleRegistrationNumber" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Marque</div>
              <input name="vehicleBrand" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Modèle</div>
              <input name="vehicleModel" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Date 1ère circulation</div>
              <input name="vehicleFirstCirculationDate" type="date" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Puissance (CV)</div>
              <input name="vehiclePower" type="number" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Nombre de places</div>
              <input name="vehicleSeatsNumber" type="number" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>N° moteur</div>
              <input name="vehicleEngineNumber" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>N° châssis</div>
              <input name="vehicleChassisNumber" className={classes.selectInputDialog} />
            </div>
            
            {/* Reference Entities Section */}
            <div>
              <div className={classes.modalLabel}>Libellé agence</div>
              <select 
                name="agencyLabel" 
                className={classes.selectInputDialog} 
                defaultValue=""
              >
                <option value="">Choisir</option>
                {agencies.map((agency) => (
                  <option key={agency._id} value={agency.name || agency.label || agency.code || agency._id}>
                    {agency.name || agency.label || agency.code || agency._id}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </AccordionPanel>
      </AccordionItem>

      {/* ========== CLAIM SECTION ========== */}
      <AccordionItem>
        <AccordionButton style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: "transparent", border: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <AccordionIcon style={{ background: "rgba(240, 243, 199, 1)", width: 25, height: 25, borderRadius: 5, color: "rgba(187, 200, 8, 1)" }} />
            <div className={classes.titreLabel}>Données Sinistre</div>
          </div>
        </AccordionButton>
        <AccordionPanel style={{ padding: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* Required Fields */}
            <div>
              <div className={classes.modalLabel}>N° Sinistre *</div>
              <input name="claimNumber" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Montant du sinistre *</div>
              <input name="claimAmount" type="number" className={classes.selectInputDialog} defaultValue="0" />
            </div>
            <div>
              <div className={classes.modalLabel}>Date survenance *</div>
              <input name="occurrenceDate" type="date" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Date déclaration *</div>
              <input name="declarationDate" type="date" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Agence déclaration *</div>
              <select 
                name="declarationAgency" 
                className={classes.selectInputDialog} 
                defaultValue=""
                onChange={handleDeclarationAgencyChange}
              >
                <option value="">Choisir</option>
                {agencies.map((agency) => (
                  <option key={agency._id} value={agency._id}>
                    {agency.name || agency.label || agency.code || agency._id}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Optional Fields */}
            <div>
              <div className={classes.modalLabel}>Type de déclaration</div>
              <select name="reportingType" className={classes.selectInputDialog} defaultValue="FirstPartyClaim">
                <option value="FirstPartyClaim">Sinistre première partie</option>
                <option value="ThirdPartyClaim">Sinistre tiers</option>
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Type de dommage</div>
              <select name="damageType" className={classes.selectInputDialog} defaultValue="MaterialDamage">
                <option value="MaterialDamage">Dommage matériel</option>
                <option value="BodilyInjury">Dommage corporel</option>
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Responsabilité</div>
              <select name="responsibility" className={classes.selectInputDialog} defaultValue="UnderInvestigation">
                <option value="UnderInvestigation">En investigation</option>
                <option value="FullResponsibility">Entière</option>
                <option value="PartialResponsibility">Partielle</option>
                <option value="NoResponsibility">Aucune</option>
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Statut</div>
              <select name="claimStatus" className={classes.selectInputDialog} defaultValue="OPEN">
                <option value="AWAITING_SERVICE_PROPOSAL">En attente de proposition de service</option>
                <option value="OPEN">Ouvert</option>
                <option value="AWAITING_ASSIGNMENT">En attente d'affectation</option>
                <option value="AWAITING_SERVICE_ASSIGNMENT">En attente d'affectation de service</option>
                <option value="AWAITING_INSPECTION">En attente d'inspection</option>
                <option value="VEHICLE_INSPECTED">Véhicule expertisé</option>
                <option value="AWAITING_QUOTATION">En attente de devis</option>
                <option value="AWAITING_QUOTATION_APPROVAL">En attente d'approbation du devis</option>
                <option value="AWAITING_PRELIMINARY_REPORT">En attente du rapport préliminaire</option>
                <option value="AWAITING_CUSTOMER_CALL">En attente d'appel client</option>
                <option value="AWAITING_APPOINTMENT_PROPOSAL">En attente de proposition RDV</option>
                <option value="AWAITING_START_OF_REPAIRS">En attente de début de réparation</option>
                <option value="AWAITING_REPAIR">En attente de réparation</option>
                <option value="AWAITING_PICTURES_BEFORE_REPARATION">En attente de photos avant réparation</option>
                <option value="AWAITING_PICTUE_AND_INVOICE">En attente de photo et facture</option>
                <option value="AWAITING_INVOICE_APPROVAL">En attente d'approbation de facture</option>
                <option value="AWAITING_PROVIDER_INVOICE">En attente de facture fournisseur</option>
                <option value="AWAITING_DISPATCH_OF_RELEASE_FORM">En attente d'envoi de bon de sortie</option>
                <option value="AWAITING_FINAL_REPORT">En attente du rapport final</option>
                <option value="AWAITING_PAYMENT_APPROVAL">En attente d'approbation de paiement</option>
                <option value="AWAITING_PAYMENT">En attente de paiement</option>
                <option value="AWAITING_CLIENT_PAYMENT">En attente de paiement client</option>
                <option value="AWAITING_PAYMENT_PROOF">En attente de preuve de paiement</option>
                <option value="AWAITING_INSPECTOR_INVOICE">En attente de facture inspecteur</option>
                <option value="AWAITING_ERECOUR_INSERSION">En attente d'insertion e-recours</option>
                <option value="ARCHIVED_CLAIM">Archivé</option>
                <option value="NO_FOLLOW_UP">Sans suite</option>
                <option value="PAYED">Payé</option>
                <option value="CLOSED">Fermé</option>
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Type de gestion</div>
              {/* stored lowercase, like the claims written by the MAE ETL */}
              <select name="managementType" className={classes.selectInputDialog} defaultValue="classique">
                {ManagementTypes.map(({ label }) => (
                  <option key={label} value={label.toLowerCase()}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Immatriculation</div>
              <input name="registrationNumber" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Conducteur</div>
              <input name="driver" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Lieu du sinistre</div>
              <input name="claimLocation" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Lieu du véhicule</div>
              <input name="carLocation" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>État du véhicule</div>
              <input name="vehicleState" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Montant de recours</div>
              <input name="recourseAmount" type="number" className={classes.selectInputDialog} defaultValue="0" />
            </div>
            <div>
              <div className={classes.modalLabel}>Type de sinistre</div>
              <select name="typeOfClaim" className={classes.selectInputDialog} defaultValue="">
                <option value="">Choisir</option>
                {claimTypeOptions.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <div className={classes.modalLabel}>Garanties (sélection multiple)</div>
              <select 
                name="coverages" 
                className={classes.selectInputDialog} 
                multiple 
                defaultValue={[]}
              >
                {coverageType.map((coverage) => (
                  <option key={coverage.code || coverage._id} value={coverage.code || coverage._id}>
                    {coverage.label}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 9, color: '#888', marginTop: 4, fontFamily: 'Poppins' }}>
                Maintenez Ctrl (Windows) ou Cmd (Mac) pour sélectionner plusieurs garanties
              </div>
            </div>
            <div>
              <div className={classes.modalLabel}>N° de déclaration</div>
              <input name="reportingNumber" className={classes.selectInputDialog} placeholder="Rempli automatiquement" />
            </div>
          </div>
        </AccordionPanel>
      </AccordionItem>

      {/* ========== THIRD PARTY SECTION ========== */}
      <AccordionItem>
        <AccordionButton style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: "transparent", border: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <AccordionIcon style={{ background: "rgba(240, 243, 199, 1)", width: 25, height: 25, borderRadius: 5, color: "rgba(187, 200, 8, 1)" }} />
            <div className={classes.titreLabel}>Données Tiers (Optionnel)</div>
          </div>
        </AccordionButton>
        <AccordionPanel style={{ padding: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div className={classes.modalLabel}>Type de tiers</div>
              <select name="thirdPartyType" className={classes.selectInputDialog} defaultValue="person">
                <option value="person">Personne physique</option>
                <option value="organization">Organisation</option>
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Rôle du tiers</div>
              <select name="thirdPartyRole" className={classes.selectInputDialog} defaultValue="AdverseDriver">
                <option value="AdverseDriver">Conducteur adverse</option>
                <option value="Pedestrian">Piéton</option>
                <option value="Passenger">Passager</option>
                <option value="Witness">Témoin</option>
                <option value="Garage">Garage</option>
              </select>
            </div>
            
            {/* Person fields */}
            <div>
              <div className={classes.modalLabel}>Prénom</div>
              <input name="thirdPartyFirstName" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Nom</div>
              <input name="thirdPartyLastName" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>CIN (Recherche tiers)</div>
              <div className={classes.policeInputContainer}>
                <input 
                  name="thirdPartyIdentityNumber"
                  className={classes.selectInputDialog} 
                  style={{ paddingRight: 40 }}
                  disabled={isPolicySearching}
                />
                <button
                  className={classes.policeSearchBtn}
                  onClick={handleThirdPartySearch}
                  disabled={isPolicySearching}
                  type="button"
                  style={{
                    opacity: isPolicySearching ? 0.6 : 1,
                    cursor: isPolicySearching ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isPolicySearching ? (
                    <span style={{ color: 'white', fontSize: 10 }}>...</span>
                  ) : (
                    <CiSearch style={{ color: 'white', width: 16, height: 16 }} />
                  )}
                </button>
              </div>
            </div>
            <div>
              <div className={classes.modalLabel}>Date de naissance</div>
              <input name="thirdPartyBirthDate" type="date" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Immatriculation tiers *</div>
              <input name="thirdPartyVehicleRegistration" className={classes.selectInputDialog} />
            </div>
            
            {/* Third Party Policy & Vehicle Information */}
            <div style={{ gridColumn: "1 / -1", marginTop: 12, marginBottom: 8 }}>
              <div style={{ 
                fontFamily: 'Poppins', 
                fontWeight: 600, 
                fontSize: 11, 
                color: 'rgba(63, 66, 84, 0.8)',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                paddingBottom: 6
              }}>
                Police et véhicule du tiers
              </div>
            </div>
            
            <div>
              <div className={classes.modalLabel}>N° Police tiers</div>
              <input name="thirdPartyPolicyNumber" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Type de police tiers</div>
              <select name="thirdPartyPolicyType" className={classes.selectInputDialog} defaultValue="INDIVIDUALPOLICY">
                <option value="INDIVIDUALPOLICY">Police individuelle</option>
                <option value="FLEETPOLICY">Police flotte</option>
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Date de souscription tiers</div>
              <input name="thirdPartySubscriptionDate" type="date" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Date d'expiration tiers</div>
              <input 
                name="thirdPartyExpirationDate" 
                type="date" 
                className={classes.selectInputDialog}
              />
            </div>
            <div>
              <div className={classes.modalLabel}>Code tiers</div>
              <input name="thirdPartyCode" className={classes.selectInputDialog} placeholder="Code du tiers (optionnel)" />
            </div>
            <div>
              <div className={classes.modalLabel}>Marque véhicule tiers</div>
              <input name="thirdPartyVehicleBrand" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Modèle véhicule tiers</div>
              <input name="thirdPartyVehicleModel" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Date 1ère circulation tiers</div>
              <input name="thirdPartyVehicleFirstCirculation" type="date" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Puissance tiers (CV)</div>
              <input name="thirdPartyVehiclePower" type="number" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>N° châssis tiers</div>
              <input name="thirdPartyVehicleChassis" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Nombre de places tiers</div>
              <input name="thirdPartyVehicleSeats" type="number" className={classes.selectInputDialog} />
            </div>
            
            {/* Third Party Address */}
            <div style={{ gridColumn: "1 / -1", marginTop: 12, marginBottom: 8 }}>
              <div style={{ 
                fontFamily: 'Poppins', 
                fontWeight: 600, 
                fontSize: 11, 
                color: 'rgba(63, 66, 84, 0.8)',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                paddingBottom: 6
              }}>
                Adresse du tiers
              </div>
            </div>
            
            <div>
              <div className={classes.modalLabel}>Adresse</div>
              <input name="thirdPartyAddress" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Ville</div>
              <input name="thirdPartyCity" className={classes.selectInputDialog} />
            </div>
            
            {/* Organization fields */}
            <div style={{ gridColumn: "1 / -1", marginTop: 12, marginBottom: 8 }}>
              <div style={{ 
                fontFamily: 'Poppins', 
                fontWeight: 600, 
                fontSize: 11, 
                color: 'rgba(63, 66, 84, 0.8)',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                paddingBottom: 6
              }}>
                Informations organisation (si applicable)
              </div>
            </div>
            
            <div>
              <div className={classes.modalLabel}>Nom de l'organisation</div>
              <input name="thirdPartyOrganizationName" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Activité</div>
              <input name="thirdPartyActivity" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>N° fiscal</div>
              <input name="thirdPartyTaxId" className={classes.selectInputDialog} />
            </div>
          </div>
        </AccordionPanel>
      </AccordionItem>

      {/* ========== EXPERTISE SECTION ========== */}
      <AccordionItem>
        <AccordionButton style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: "transparent", border: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <AccordionIcon style={{ background: "rgba(240, 243, 199, 1)", width: 25, height: 25, borderRadius: 5, color: "rgba(187, 200, 8, 1)" }} />
            <div className={classes.titreLabel}>Données Expertise (Optionnel)</div>
          </div>
        </AccordionButton>
        <AccordionPanel style={{ padding: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div className={classes.modalLabel}>Type de mission</div>
              <select name="missionType" className={classes.selectInputDialog} defaultValue="">
                <option value="">Choisir</option>
                <option value="Première Expertise">Première Expertise</option>
                <option value="Deuxième Expertise">Deuxième Expertise</option>
                <option value="Contre-Expertise">Contre-Expertise</option>
                <option value="Arbitrage">Arbitrage</option>
                <option value="Rapport De Carrence">Rapport De Carrence</option>
                <option value="Expertise Judiciare">Expertise Judiciare</option>
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Expert assigné</div>
              <select name="expert" className={classes.selectInputDialog} defaultValue="">
                <option value="">Choisir</option>
                {expertOptions.map((expert) => (
                  <option key={expert.value} value={expert.value}>
                    {expert.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Date de la mission</div>
              <input name="expertiseDate" type="date" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Statut de la mission</div>
              <select name="expertiseStatus" className={classes.selectInputDialog} defaultValue="OPEN">
                <option value="OPEN">Ouverte</option>
                <option value="ON_GOING">En cours</option>
                <option value="DECLINED">Déclinée</option>
                <option value="CLOSED">Fermée</option>
              </select>
            </div>
            <div>
              <div className={classes.modalLabel}>Lieu de l'expertise</div>
              <input name="expertiseLocation" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Valeur actuelle du véhicule</div>
              <input name="currentCarValue" type="number" className={classes.selectInputDialog} />
            </div>
            <div>
              <div className={classes.modalLabel}>Valeur initiale du véhicule</div>
              <input name="initialCarValue" type="number" className={classes.selectInputDialog} />
            </div>
          </div>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default NouveauSinistreForm;

